import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import "./legal.css";

const privacySections = [
  {
    title: "1. Information We Collect",
    paragraphs: ["We collect information to provide and improve Prep Guardian."],
    subsections: [
      {
        title: "a. Personal Information",
        paragraphs: [
          "When you create an account or use certain features, we may collect:",
        ],
        bullets: [
          "Name",
          "Email address",
          "Account login details",
          "Profile or preferences (if applicable)",
        ],
      },
      {
        title: "b. Usage Data",
        paragraphs: ["Automatically collected data includes:"],
        bullets: [
          "Device type, operating system, and version",
          "IP address and approximate location",
          "App usage activity, performance data, and crash reports",
        ],
      },
      {
        title: "c. Optional or Permission-Based Data",
        paragraphs: ["With your consent, the App may access:"],
        bullets: [
          "Notifications or reminders",
          "Camera, microphone, or photo library (if required for features)",
        ],
        closingParagraphs: [
          "You can revoke these permissions anytime via your device settings.",
        ],
      },
    ],
  },
  {
    title: "2. How We Use Your Information",
    paragraphs: ["We use your information to:"],
    bullets: [
      "Provide, maintain, and improve the App",
      "Personalize your experience",
      "Communicate important updates and security alerts",
      "Respond to inquiries and support requests",
      "Analyze performance and usage trends",
      "Comply with legal and regulatory requirements",
    ],
    closingParagraphs: ["We do not sell or rent your personal information."],
  },
  {
    title: "3. Data Sharing and Disclosure",
    paragraphs: ["We may share information only as necessary:"],
    bullets: [
      "With Service Providers: For hosting, analytics, and support services that help operate the App.",
      "For Legal Reasons: If required by law, regulation, or legal process.",
    ],
    closingParagraphs: [
      "All partners and service providers must follow strict data-protection obligations consistent with Apple’s App Store Review Guidelines and Google Play Developer Policies.",
    ],
  },
  {
    title: "4. Data Retention",
    paragraphs: [
      "We retain personal data only as long as needed for the purposes described or as required by law. You may request deletion of your account and data at any time (see Section 9).",
    ],
  },
  {
    title: "5. Data Security",
    paragraphs: [
      "We use industry-standard administrative, technical, and physical safeguards to protect your information. Despite our efforts, no online service can be guaranteed 100% secure.",
    ],
  },
  {
    title: "6. Your Rights",
    paragraphs: [
      "Depending on your location (e.g., GDPR, CCPA jurisdictions), you may:",
    ],
    bullets: [
      "Access, correct, or delete your data",
      "Withdraw consent or object to processing",
      "Request data portability",
    ],
    closingParagraphs: [
      'To exercise these rights, email support@prepguardian.com with “Privacy Request” in the subject line.',
    ],
  },
  {
    title: "7. Children’s Privacy",
    paragraphs: [
      "Prep Guardian is not directed toward children under 13 years old (or the age required by local law). We do not knowingly collect data from children. If we discover such data, we delete it promptly.",
    ],
  },
  {
    title: "8. International Data Transfers",
    paragraphs: [
      "Your information may be processed in other countries where we or our service providers operate. We ensure adequate safeguards are in place to protect your data.",
    ],
  },
  {
    title: "9. Data Deletion Policy",
    paragraphs: [
      "You may request permanent deletion of your Prep Guardian account and associated personal information at any time by contacting us at support@prepguardian.com.",
      "Upon verifying your identity:",
    ],
    bullets: [
      "Your account and related personal data will be permanently deleted from our servers within 30 days.",
      "Backups containing your data will be securely erased within 90 days.",
      "Some anonymized or aggregated data (non-identifiable) may be retained for analytics or legal obligations.",
    ],
    closingParagraphs: [
      "You can also delete local app data manually by uninstalling the App from your device.",
    ],
  },
  {
    title: "10. Compliance with Apple and Google Policies",
    paragraphs: ["Prep Guardian complies with:"],
    bullets: [
      "Apple App Store Review Guidelines (Section 5 – Legal): We provide clear disclosure of data collection, use, and retention practices and honor all user deletion requests.",
      "Google Play Developer Program Policies: We display this Privacy Policy in the App and Play Store listing, and we provide an in-app path for users to access it.",
    ],
    closingParagraphs: [
      "We honor Data Safety declarations and only collect data necessary for the App’s functionality.",
    ],
  },
  {
    title: "11. Changes to This Policy",
    paragraphs: [
      'We may update this Privacy Policy periodically. The latest version will always be accessible within the App and on our website, showing the “Last Updated” date. Continued use of the App means you accept any changes.',
    ],
  },
  {
    title: "12. Contact Us",
    paragraphs: [
      "If you have any questions or privacy requests, contact us at:",
      "📧 support@prepguardian.com",
      "🌐 www.prepguardian.com",
    ],
  },
];

const agreementSections = [
  {
    title: "",
    paragraphs: [
      "SUBSCRIPTION SERVICES AGREEMENT (this “Agreement”)",
      "The Prep Guardian app and any and all related products or services are exclusively licensed and operated by Compu-Imaging, Inc., a Florida corporation (“our”, “us”, “we”). We are the developers of the Apps. This Agreement outlines our agreement with you and the user or any third party based on you or the user’s use of the Apps, or our products or services. You and all users of the Apps and any and all related products or services affirm that you have fully read and understand this Agreement and agree to these terms and conditions. Sometimes additional terms or product requirements may apply and they will become part of your agreement with us if you use our Apps, products or services or the products of a third party that we make available to you.",
      "We collect Personal Information and Use Cookies as outlined in our Privacy Policy.",
      "Do not submit confidential, proprietary, or personal information to us via email.",
    ],
  },
  {
    title: "Scope of License",
    paragraphs: [
      "We grant you a limited revocable, non-exclusive, non-assignable, non-transferable, non-sublicensable, license to use the App specified in your purchase order. Users are subject to the use of the App on any iOS products or Android Products that the end user owns or controls and as permitted by the usage rules set forth in the App Store or Google Play Terms of Service. Notwithstanding the fact that this Agreement covers the use of both Apps, this Agreement shall not entitle you to use an App that is not purchased in your order with us.",
      "You and all users are responsible for removing and uninstalling the App and must immediately cease downloading the App or communicating with or using the Apps if you do not agree to this Agreement.",
    ],
  },
  {
    title: "Use of the Apps, Products, and Services at Your Own Risk/Disclaimers",
    paragraphs: [
      "Your access to and use of our website(s), any content on our website(s) the Apps, any product, or Services used by any user or available to any user, is at you or any users own risk, INCLUDING WITH REGARD TO THE PERFORMANCE OF THE APPS, OR PRODUCTS, OR SERVICES, AND WITHOUT LIMITATION, EXPRESS OR IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE OR NON-INFRINGEMENT. ANY RELIANCE ON THE WEBSITES, APPS, PRODUCTS, OR THE SERVICES IS AT YOUR OWN RISK, AND WE DO NOT AND SHALL NOT ACCEPT ANY LIABILITY FOR THE WEBSITE, APPS, PRODUCTS, OR THE SERVICES, INCLUDING WITHOUT LIMITATION ANY MISTAKES OR INACCURACIES IN OR MADE AVAILABLE THROUGH THE WEBSITE, APPS, PRODUCTS OR SERVICES WE OFFER OR OFFERED FROM ANY THIRD PARTY. WHERE PERMITTED BY LAW, WE EXPRESSLY DISCLAIM ANY WARRANTIES. WE SHALL HAVE NO RESPONSIBILITY FOR ANY HARM TO YOU OR USERS, OR THIRD PARTIES CLAIMING THROUGH YOU OR USERS, FROM LOSS OF DATA OR ANY OTHER HARM TO YOU OR TO ANY THIRD PARTY, INCLUDING WITHOUT LIMITATION, ANY BODILY HARM THAT MAY RESULT FROM YOUR ACCESS TO THE WEBSITE, APP, PRODUCTS, OR SERVICES, LOSS OF LABEL INFORMATION, INACCURATE LABEL INFORMATION, OR ANY INFORMATION OR RELIANCE ON ANY INFORMATION WITH RESPECT TO ANY FOOD PRODUCTS. WHEN PERMITTED BY LAW, WE, AND OUR SUPPLIERS AND DISTRIBUTORS, SHALL NOT BE LIABLE FOR LOST PROFITS, REVENUES, OR DATE, FINANCIAL LOSSES, OR INDIRECT, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES. OUR TOTAL LIABILITY, COLLECTIVELY, IS LIMITED TO THE AMOUNT YOU PAID US TO USE THE APP, PRODUCTS, AND/OR SERVICES (OR, IF WE CHOOSE, TO SUPPLYING YOU THE PRODUCTS OR SERVICES AGAIN).",
    ],
  },
  {
    title: "Your Data",
    paragraphs: [
      "All data uploaded by you or any third party on your behalf to the Apps remains the property your property, as between you and us. You grant us the right to use such data solely in connection with the Apps and the services to be provided in connection with the Apps. During the term of this Agreement, you may export your data as permitted by the Apps.",
      "You may allow your employees and contractors to access the Apps in compliance with the terms of this Agreement, which access must be solely for your benefit. You are responsible for the compliance with this Agreement by your employees and contractors.",
      "We may provide you the ability to store data collected through the Apps on our servers. If we determine in our sole discretion, that you are storing an unreasonably large amount of data, which amount is greater than 10 gigabytes per license/store as set forth in your order, then we may, upon prior written notice to you impose additional charges or take other steps to mitigate this burden.",
    ],
  },
  {
    title: "Use of Our Products or Services on Behalf of a Third Party",
    paragraphs: [
      "If you use our Apps, products or services on a behalf of a third party, that user is bound by this Agreement.",
    ],
  },
  {
    title: "Modification of Apps, Products or Services",
    paragraphs: [
      "We reserve the right, at any time, to modify, suspend, or discontinue the Apps or any products or services related to the Apps or use of the Apps, including our websites or relationships with third parties, with or without notice to you or any user. We and any third party may remove content available related to the Apps, products, including labels, in whole or in part, or we may modify or remove any content or options from the Apps, or the products, including labels, or services we make available to you without notice to you. You agree we are not liable for any such modification, suspension, discontinuance or removal.",
    ],
  },
  {
    title: "Intellectual Property Rights",
    paragraphs: [
      "You acknowledge that all intellectual property rights in and to the Apps belong to us or our licensees, and the rights granted herein are not sold to you and you have no rights to, or in the Apps or its content other than the right to use the Apps in accordance with this Agreement. All trademarks, service marks, trade names, slogans, and logos are the exclusively property of Compu-Imaging, Inc. You acknowledge you do not have the right to access to the Apps in source code form.",
    ],
  },
  {
    title: "Our Distributors, App Stores, Third Parties",
    paragraphs: [
      "You acknowledge and agree that the availability of the Apps may be dependent on its availability through the store from which you received the Apps, e.g., the Apple App Store, Google Play, and/or other app stores (“App Stores”). You acknowledge that this Agreement is between you and us and not with the App Stores. We, not the App Stores, are solely responsible for the Apps, and any and all related products and services, including the content thereof, maintenance, support services, and warranty therefor, and addressing any claims relating thereto (e.g., product liability, legal compliance, or intellectual property infringement). You agree to pay all fees charged by the App Stores in connection with the Apps. You agree to comply with, and your license to use the Apps is conditioned upon your compliance with, all applicable agreements, terms of use/service, and other policies of the App Stores. You acknowledge that the App Stores (and their subsidiaries) are a third-party beneficiary of this Agreement and will have the right to enforce this Agreement.",
    ],
  },
  {
    title: "Third Party Sites & Ads",
    paragraphs: [
      "App related websites, products, or services may contain links to third party sites and advertisements for third parties (“Third Party Sites & Ads”). We have designed the Prep Guardian systems for use with most iPhone or Android phones or devices, such as the following:",
      "The app supports devices running iOS 15 and above. Older devices are not supported.",
      "The app supports devices running Android 9 (API level 28) and above. Older versions are not supported.",
      "Such Third Party Sites & Ads are not under our control and we are not responsible for any Third Party Sites & Ads. We provide these Third Party Sites & Ads only as a convenience and do not review, approve, monitor, endorse, warrant, or make any representations with respect to Third Party Sites & Ads. Advertisements and other information provided by Third Party Sites & Ads may not be wholly accurate. You acknowledge sole responsibility for and assume all risk arising from your use of any such websites or resources. When you link to a Third Party Site, the applicable service provider’s terms and policies, including privacy and data gathering practices govern. You should make whatever investigation you feel necessary or appropriate before proceeding with any transaction with any third party. Your transactions and other dealings with Third Party Sites & Ads that are found on or through the Site, Apps or Services, including payment and delivery of related goods or services, are solely between you and such merchant or advertiser.",
    ],
  },
  {
    title: "Release",
    paragraphs: [
      "You hereby release us from claims, demands any and all losses, damages, rights, claims, and actions of any kind including personal injuries, death, and property damage, that is either directly or indirectly related to or arises from any interactions with or conduct of any App Store, any other product or service users, or any Third Party Sites & Ads.",
      "If you are a California resident, you hereby waive California civil code section 1542 in connection with the foregoing, which states: “a general release does not extend to claims which the creditor does not know or suspect to exist in his favor at the time of executing the release, which, if known by him must have materially affected his settlement with the debtor.”",
    ],
  },
  {
    title: "Payment Terms/Fees for the App, Products, or Services",
    paragraphs: [
      "We may suspend or terminate the Apps, or any or all products, or services provided to you if you are delinquent in any payment or other financial obligation to us. You are solely responsible for ensuring we have accurate billing information for you or your users of our Apps, or related products, or services.",
    ],
  },
  {
    title: "Mobile App",
    paragraphs: [
      "You acknowledge that the Apps are provided over the internet and mobile networks and so the quality and availability of the Apps may be affected by factors outside our reasonable control. Accordingly, we do not accept any responsibility for any connectivity issues that you may experience when using the Apps or our website(s) for the purpose of purchasing products or services. In addition, you acknowledge and agree that you (and not us) are responsible for obtaining and maintaining all telecommunications, broadband and computer hardware, equipment and services needed to access and use the Apps, and for paying any data charges that may be charged to you by your network provider in connection with your use of the Apps. In the event you choose to share information from the Apps by utilizing SMS/text functionality, you acknowledge and agree that you (and not us) are responsible for paying any related fees that may be charged to you by your network or telecommunications provider.",
    ],
  },
  {
    title: "Term and Termination",
    paragraphs: [
      "If you violate any of the terms of this Agreement or fail to make the required payments pursuant to your order with us, your permission to use our website(s), the Apps, and any related product or service will automatically terminate, at our option. We reserve the right to revoke your access to and use of the website(s), the Apps, and any related product or service at any time, with or without cause.",
    ],
  },
  {
    title: "Confidentiality",
    paragraphs: [
      "Both parties agree that they will not at any time disclose, give, or transmit in any manner or for any purpose, the Confidential Information received from the other party to any other party or use such Confidential Information for its own benefit or the benefit of anyone else, or for any purpose not contemplated in this Agreement; provided that the foregoing will not apply to the extent any Confidential Information (i) becomes part of the public domain through no act or omission of the receiving party, (ii) is independently discovered or developed by the receiving party, (iii) is rightfully obtained from a third party without any obligation of confidentiality, or (iv) is already known by the receiving party without any obligation of confidentiality prior to obtaining the Confidential Information.",
      "For purposes of this Agreement, the term “Confidential Information” shall mean the following: Any information, business plan, concept, idea, know-how, process, technique, program, design, formula, algorithm or work-in-process, any engineering, marketing, technical, financial, data, or sales information, or any information regarding suppliers, customers, employees, investors, or business operations, and any other information or materials in any form and medium, whether disclosed orally or electronically, and whether tangible or intangible, or which is otherwise learned or disclosed in connection with the use of the Apps.",
      "In order to protect your Confidential Information, we: (A) implement and maintain all reasonable security measures appropriate to the nature of the Confidential Information including, without limitation, technical, physical, administrative, and organizational controls, and will maintain the confidentiality, security, and integrity of such Confidential Information; (B) implement and maintain industry standard systems and procedures for detecting, preventing, and responding to attacks, intrusions, or other systems failures and regularly tests, or otherwise monitors the effectiveness of the safeguards’ key controls, systems, and procedures; (C) identify reasonably foreseeable internal and external risks to the security, confidentiality, and integrity of your Confidential Information that could result in the unauthorized disclosure, misuse, alteration, destruction, or other compromise of such information, and assesses the sufficiency of any safeguards in place to control these risks; and (D) designate an employee or employees to coordinate implementation and maintenance of (A) through (C) above.",
      "If we know that your Confidential Information has been accessed, disclosed, or acquired without proper authorization and contrary to the terms of this Agreement, we will promptly alert you of any such data breach, and immediately take such actions as may be necessary to preserve forensic evidence and eliminate the cause of the data breach. We will provide you with all available information reasonably necessary to enable you to fully understand the nature and scope of the data breach. To the extent that you, in your sole reasonable discretion, deems warranted, may provide notice to any or all parties affected by any data breach. In such case, we will consult with you in a timely fashion regarding appropriate steps required to notify third parties. We will provide you with information about what we have done or plan to do to minimize any harmful effect of the unauthorized use or disclosure of, or access to, Confidential Information.",
    ],
  },
  {
    title: "Miscellaneous",
    paragraphs: [
      "Each party shall comply with all laws, rules and regulations, if any, applicable to it in connection with the Apps and this Agreement.",
      "Notice shall be deemed given (i) upon receipt when delivered personally, (ii) upon written verification of receipt from overnight courier, or (iii) upon verification of receipt of registered or certified mail.",
      "You may not assign your right to use the Apps or your agreement under this Agreement without our prior written consent. Any consent required will not be unreasonably withheld. Any assignment in violation of this section will be null and void.",
      "This Agreement is governed by the laws of the State of Florida (without regard to conflicts of law principles) for any dispute between the parties or relating in any way to the subject matter of this Agreement. In connection with any action or proceeding arising out of or relating to this Agreement, each of the parties submits to the jurisdiction of any competent court sitting in Charlotte County, Florida having subject matter jurisdiction. Any action or proceeding initially commenced by either party shall be commenced solely in a competent court located in Charlotte County, Florida. Each of the parties hereto waives any defense of inconvenient forum to the maintenance of any action or proceeding so brought. Each party agrees that a final judgment in any action or proceeding so brought shall be conclusive and may be enforced on the judgment in any manner provided by law or at equity.",
      "This Agreement and your order from us, including the terms and conditions attached thereto (the “Terms and Conditions”), constitute the entire agreement between the you and us and supersede any prior or contemporaneous negotiations or agreements, whether oral or written, related to this subject matter. You are not relying on any representation concerning this subject matter, oral or written, not included in this Agreement or the Terms and Conditions. No representation, promise, or inducement not included in this Agreement are binding. No modification or waiver of any term of the terms of this Agreement is effective unless both parties sign it, however this agreement may be modified through an online process provided by us.",
      "The failure to require performance of any provision shall not affect a party’s right to require performance at any time thereafter; nor shall waiver of a breach of any provision constitute a waiver of the provision itself.",
      "If any provision is adjudged by a court of competent jurisdiction to be unenforceable, invalid or otherwise contrary to law, such provision shall be interpreted so as to best accomplish its intended objectives and the remaining provisions shall remain in full force and effect.",
      "Any terms, that by their nature survive termination of this Agreement for a party to assert its rights and receive the protections of this Agreement, will survive (including, without limitation, the confidentiality terms).",
      "The parties hereto are and shall remain independent contractors and nothing herein shall be deemed to create any agency, partnership, or joint venture relationship between the parties. Neither party shall be deemed to be an employee or legal representative of the other nor shall either party have any right or authority to create any obligation on behalf of the other party.",
      "The Agreement is not intended to benefit, nor shall it be deemed to give rise to, any rights in any third party.",
      "Neither party shall be liable for failing or delaying performance of its obligations (except for the payment of money) resulting from governmental action, acts of terrorism, earthquake, fire, flood or other acts of God, labor conditions, power failures, and Internet disturbances.",
      "The Agreement shall be binding on and inure to the benefit of each of the parties and their respective successors and assigns.",
      "We reject additional or conflicting terms of your form-purchasing document, if any.",
    ],
  },
];

const documentConfig = {
  "/privacy-policy": {
    eyebrow: "Legal",
    title: "Privacy Policy",
    updatedAt: "Last Updated: May 1, 2026",
    intro:
      "Compu-Imaging, Inc. (“we,” “our,” or “us”) respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your data when you use our mobile application, Prep Guardian (“App”) on iOS and Android devices.",
    sections: privacySections,
  },
  "/service-agreement": {
    eyebrow: "Legal",
    title: "Subscription Services Agreement",
    updatedAt: "",
    intro: "",
    sections: agreementSections,
  },
};

const LegalDocument = () => {
  const location = useLocation();
  const content = documentConfig[location.pathname];

  if (!content) return null;

  return (
    <section className="legal-page">
      <Container fluid className="p-0">
        <Row className="m-0 legal-shell">
          <Col lg={5} className="px-0 legal-visual-col">
            <div className="legal-visual">
              <Link to="/" className="legal-logo">
                <img src="/images/logo.svg" alt="Prep Guardian" />
              </Link>
              {content.eyebrow ? (
                <div className="legal-badge">{content.eyebrow}</div>
              ) : null}
              <h1>{content.title}</h1>
              {content.intro ? <p>{content.intro}</p> : null}
              {content.updatedAt ? (
                <div className="legal-meta">{content.updatedAt}</div>
              ) : null}
              <img
                src="/images/auth-img.png"
                alt="Prep Guardian"
                className="legal-hero-image"
              />
            </div>
          </Col>
          <Col lg={7} className="legal-content-col">
            <div className="legal-card">
              <div className="legal-topbar">
                <Link to="/" className="legal-back-link">
                  {/* Back to Login */}
                </Link>
                <a href="mailto:support@prepguardian.com" className="legal-help">
                  support@prepguardian.com
                </a>
              </div>

              {content.sections.map((section) => (
                <article
                  className="legal-section"
                  key={section.title || section.paragraphs?.[0]}
                >
                  {section.title ? <h2>{section.title}</h2> : null}
                  {section.paragraphs?.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                  {section.subsections?.map((subsection) => (
                    <div className="legal-subsection" key={subsection.title}>
                      <h3>{subsection.title}</h3>
                      {subsection.paragraphs?.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                      {subsection.bullets?.length ? (
                        <ul>
                          {subsection.bullets.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      ) : null}
                      {subsection.closingParagraphs?.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                    </div>
                  ))}
                  {section.bullets?.length ? (
                    <ul>
                      {section.bullets.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ) : null}
                  {section.closingParagraphs?.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </article>
              ))}
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default LegalDocument;
